import { Layout } from "providers/Layout";
import { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Table } from "shared/app/Table";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  InputSize,
  Show
} from "shared/components";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Pencil02 } from "untitledui-js-base";
import { useNavigate } from "react-router-dom";
import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";
import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { formatTitle } from "utils/formatTitle";
import ArrowSplit from "shared/icons/arrowSplit.svg?react";
import { RegroupWarningModal } from "./RegroupWarningModal";
import { plural } from "pluralize";
import { useAuthContext } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { getStateLabel } from "utils/stateCodes";
import { PropertyGroup } from "types/property-group";
import { PropertyGroupOptionBadgeList } from "shared/components/PropertyGroupOptionBadgeList";
import { Template } from "types/landlord/verificationTemplate";

interface PropertyGroupsTableProps {
  propertyGroups: PropertyGroup[];
  totalProperties: number;
  availableSplitOptions: PropertyGroupSplitBy[];
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
}

export const PropertyGroupsTable: FC<PropertyGroupsTableProps> = ({
  availableSplitOptions,
  propertyGroups,
  totalProperties,
  searchTerm,
  setSearchTerm,
  isLoading
}) => {
  const navigate = useNavigate();
  const [isRegroupModalOpen, setIsRegroupModalOpen] = useState(false);
  const { type } = useAuthContext();
  const { properties, property, Property, Properties } =
    useUnitsPropertiesDictionary();

  const columns = useMemo<ColumnDef<PropertyGroup>[]>(() => {
    const splitOptionColumns = availableSplitOptions.map((option, index) => ({
      id: `${option}`,
      size: 160,
      header: formatTitle(propertyGroupTitleMap[option]),
      cell: ({ row }) => {
        const splitDataItem = row.original.split_data_chain?.[index + 1];
        if (!splitDataItem) return null;

        const isOther = splitDataItem.is_other;
        const values = splitDataItem.split_values || [];

        const formatValue = (value: string) => {
          if (option === PropertyGroupSplitBy.STATE)
            return getStateLabel(value);
          if (option === PropertyGroupSplitBy.CITY) return formatTitle(value);
          return value;
        };

        const optionTitle = propertyGroupTitleMap[option] || option;

        return (
          <PropertyGroupOptionBadgeList
            values={values}
            formatValue={formatValue}
            isOther={isOther}
            otherTitle={plural(optionTitle)}
          />
        );
      }
    }));

    return [
      {
        id: "fixed_index",
        accessorKey: "index",
        header: "#",
        size: 30,
        cell: ({ row }) => (
          <div className="text-content-1000 font-medium">
            {row.original.index + 1}
          </div>
        )
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Group name",
        cell: ({ row }) => {
          return (
            <p className="text-content-1000 font-medium">{row.original.name}</p>
          );
        }
      },
      ...splitOptionColumns,
      {
        id: "totalProperties fixed_width",
        accessorKey: "total_properties",
        header: `${Properties}`,
        size: 50,
        cell: ({ row }) => (
          <div className="text-content-1000">
            {row.original.total_properties || 0}
          </div>
        )
      },
      {
        accessorKey: "verification_template",
        header: "Template",
        cell: ({ row }) => {
          const template = row.original.verification_template as
            | Template
            | undefined;
          return (
            <div className="text-content-1000 font-medium">
              {template?.name || "No template"}
            </div>
          );
        }
      },
      {
        id: "popover-action",
        size: 48,
        accessorFn: (row) => row.id,
        header: "",
        cell: (p) => {
          const id = p.getValue();
          return (
            <ContextMenuPopover
              contentClassName="pr-8"
              actions={[
                {
                  title: (
                    <span className="flex gap-2 items-center">
                      <Pencil02 size="18" className="stroke-content-200" />
                      Edit details
                    </span>
                  ),
                  onClick: () => {
                    navigate(`/property-groups/${id}`);
                  }
                }
              ]}
            />
          );
        }
      }
    ];
  }, [availableSplitOptions]);

  const table = useReactTable({
    data: propertyGroups,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Layout sectionClassName="space-y-4 h-full">
      <div className="flex items-center justify-between">
        <div className="space-y-3">
          <h1 className="text-2xl font-bold">{Property} groups</h1>
          <p className="text-sm text-content-400">
            {totalProperties} {properties}
          </p>
        </div>
        <Show if={type.isAdmin || type.isOwner}>
          <div className="flex gap-2">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              iconPosition="right"
              onClick={() => {
                localStorage.setItem(
                  "existing_property_groups",
                  JSON.stringify(propertyGroups)
                );
                navigate(
                  `/property-groups/configuration?step=${availableSplitOptions[0]}`
                );
              }}
            >
              Edit groups
            </Button>

            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={ArrowSplit}
              iconPosition="right"
              onClick={() => setIsRegroupModalOpen(true)}
            >
              Regroup {properties}
            </Button>
          </div>
        </Show>
      </div>

      <div className="max-w-[271px] pt-2">
        <Input
          id="search"
          autoComplete="off"
          placeholder="Search group"
          size={InputSize.SM}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <Table
        rowHeight="90px"
        emptyStateText={
          searchTerm
            ? `${property} group will appear here`
            : `Add ${property} groups to be able to invite applicants for screening`
        }
        emptyStateTitle={
          searchTerm ? "No matches found" : `No ${property} groups`
        }
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/property-groups/${row}`)}
        table={table}
        tableClassName="h-[calc(100vh-200px)]"
      />

      <RegroupWarningModal
        isOpen={isRegroupModalOpen}
        onClose={() => setIsRegroupModalOpen(false)}
      />
    </Layout>
  );
};
